import { useCallback, useMemo, useState } from 'react';
import { useResultData } from '../../context';
import { tokens } from '../../tokens';
import Row from './Row';
import ExpandedRow from './ExpandedRow';
import ExpandedRowMrc from './ExpandedRowMrc';
import { TxFilters } from './FilterRow';
import { txIsCustom } from '../../txHelpers';

interface Props {
  /** Filter state — owned by Result so the filter row can sit above
   * the live-state section. */
  filters: TxFilters;
}

const TransactionList = ({ filters }: Props) => {
  const { transactionList, mrcByTxId } = useResultData();
  const [expandedTxId, setExpandedTxId] = useState<number | null>(null);
  const [mode, setMode] = useState<'standard' | 'mrc'>('standard');

  const handleToggleExpand = useCallback((id: number) => {
    setExpandedTxId((prev) => (prev === id ? null : id));
    setMode('standard');
  }, []);

  const handleNavigate = useCallback((id: number) => {
    setExpandedTxId(id);
    setMode('standard');
  }, []);

  const visibleList = useMemo(() => {
    const startMs = filters.dateStart
      ? new Date(filters.dateStart).getTime()
      : null;
    const endMs = filters.dateEnd
      ? new Date(filters.dateEnd).getTime() + 86_400_000
      : null;
    return transactionList.filter((tx) => {
      if (tx.orderType === 'BUY' && !filters.buy) return false;
      if (tx.orderType === 'SELL' && !filters.sell) return false;
      // Interest has its own chip (off by default — it is credited daily and
      // would bury every real trade); every other non-trade row rides the one
      // "balance" filter.
      const typeKey =
        tx.type === 'tp'
          ? 'tp'
          : tx.type === 'sl'
          ? 'sl'
          : tx.type === 'dist'
          ? 'dist'
          : tx.type === 'interest'
          ? 'interest'
          : txIsCustom(tx.type)
          ? 'balance'
          : 'core';
      if (!filters[typeKey]) return false;
      if (startMs !== null && tx.timestamp < startMs) return false;
      if (endMs !== null && tx.timestamp > endMs) return false;
      return true;
    });
  }, [transactionList, filters]);

  const ids = visibleList.map((tx) => tx.id);
  const expandedIndex = expandedTxId !== null ? ids.indexOf(expandedTxId) : -1;
  // The list is newest-first (highest # at the top). NEXT navigates up to a
  // higher number; PREV navigates down to a lower number.
  const nextId = expandedIndex > 0 ? ids[expandedIndex - 1] : null;
  const prevId =
    expandedIndex >= 0 && expandedIndex < ids.length - 1
      ? ids[expandedIndex + 1]
      : null;

  return (
    <div
      style={{
        background: tokens.backgroundBase,
        padding: 0,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      {visibleList.map((tx, index) => {
        const isExpanded = expandedTxId === tx.id;
        const hasMrc = !!mrcByTxId[tx.id];

        if (!isExpanded) {
          return (
            <Row
              key={tx.id}
              tx={tx}
              isFirst={index === 0}
              onToggle={handleToggleExpand}
            />
          );
        }

        if (mode === 'mrc' && hasMrc) {
          return (
            <ExpandedRowMrc
              key={tx.id}
              txId={tx.id}
              isFirst={index === 0}
              onCollapse={() => setExpandedTxId(null)}
              onToggleMode={() => setMode('standard')}
            />
          );
        }
        return (
          <ExpandedRow
            key={tx.id}
            txId={tx.id}
            isFirst={index === 0}
            onCollapse={() => setExpandedTxId(null)}
            onToggleMode={hasMrc ? () => setMode('mrc') : undefined}
            onPrev={prevId !== null ? () => handleNavigate(prevId) : undefined}
            onNext={nextId !== null ? () => handleNavigate(nextId) : undefined}
            prevId={prevId}
            nextId={nextId}
          />
        );
      })}
    </div>
  );
};

export default TransactionList;
